import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCalendar,
  FaTag,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaPlay,
} from "react-icons/fa";
import { ImEnlarge2 } from "react-icons/im";
import axios from "axios";
import { BASE_URL } from "../BaseURL";
import "../Assets/WebsiteCss/ProjectDetails.css";

function ProjectDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [categoryMap, setCategoryMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);

  const getEmbedUrl = (url) => {
    if (!url) return "";
    if (url.includes("embed")) return url;

    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  const getVideoThumbnail = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    return match
      ? `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`
      : "";
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const projectRes = await axios.get(`${BASE_URL}/api/projects/${id}`);
        const projectData =
          projectRes.data.data || projectRes.data.project || projectRes.data;

        if (!projectData) {
          throw new Error("Project not found");
        }

        setProject(projectData);

        const categoryRes = await axios.get(
          `${BASE_URL}/api/category/get-category`,
        );
        const categories = categoryRes.data.data || categoryRes.data;

        const map = {};
        categories.forEach((cat) => {
          map[cat._id] = cat.categoryName;
        });
        setCategoryMap(map);

        setError(null);
      } catch (err) {
        console.error(err);
        setError("Failed to load project details");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  // Keyboard navigation for both main image and lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && lightboxOpen) {
        setLightboxOpen(false);
      } else if (e.key === "ArrowLeft") {
        navigateImage("prev");
      } else if (e.key === "ArrowRight") {
        navigateImage("next");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, selectedImage, project]);

  // Lock body scroll when lightbox open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [lightboxOpen]);

  const navigateImage = (direction) => {
    if (!project?.images) return;

    if (direction === "next") {
      setSelectedImage((prev) =>
        prev === project.images.length - 1 ? 0 : prev + 1,
      );
    } else {
      setSelectedImage((prev) =>
        prev === 0 ? project.images.length - 1 : prev - 1,
      );
    }
  };

  const openLightbox = (index) => {
    setSelectedImage(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading project details...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="error-container">
        <h2>{error || "Project not found"}</h2>
        <button className="btn-back" onClick={() => navigate("/projects")}>
          <FaArrowLeft /> Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="project-details-page">
      {/* BACK BUTTON */}
      <div className="back-navigation">
        <button className="btn-back" onClick={() => navigate("/projects")}>
          <FaArrowLeft /> Back to Projects
        </button>
      </div>

      {/* PROJECT HEADER */}
      <div className="project-header">
        <h1 className="project-title">{project.title}</h1>
        <div className="project-meta">
          <span className="category-badge">
            <FaTag /> {categoryMap[project.category] || "Unknown Category"}
          </span>
          <span className="project-date">
            <FaCalendar />{" "}
            {new Date(project.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
        </div>
      </div>

      <div className="project-content">
        {/* LEFT SIDE - MEDIA */}
        <div className="media-section">
          {/* IMAGE GALLERY */}
          {project.images?.length > 0 && (
            <div className="gallery-section">
              <div className="main-image-container">
                <img
                  src={`${BASE_URL}${project.images[selectedImage]}`}
                  alt={project.title}
                  className="main-image"
                  onClick={() => openLightbox(selectedImage)}
                />

                {/* Navigation Arrows */}
                {project.images.length > 1 && (
                  <>
                    <button
                      className="image-nav-btn prev"
                      onClick={() => navigateImage("prev")}
                      aria-label="Previous image"
                    >
                      <FaChevronLeft />
                    </button>
                    <button
                      className="image-nav-btn next"
                      onClick={() => navigateImage("next")}
                      aria-label="Next image"
                    >
                      <FaChevronRight />
                    </button>
                  </>
                )}

                <div className="image-counter">
                  {selectedImage + 1} / {project.images.length}
                </div>
                <button
                  className="zoom-hint"
                  onClick={() => openLightbox(selectedImage)}
                >
                  <ImEnlarge2 />
                </button>
              </div>

              {/* THUMBNAILS */}
              {project.images.length > 1 && (
                <div className="thumbnails-container">
                  <div className="thumbnails-scroll">
                    {project.images.map((img, index) => (
                      <div
                        key={index}
                        className={`thumbnail ${
                          selectedImage === index ? "active" : ""
                        }`}
                        onClick={() => setSelectedImage(index)}
                      >
                        <img
                          src={`${BASE_URL}${img}`}
                          alt={`View ${index + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VIDEO SECTION */}
          {project.videos?.length > 0 && (
            <div className="videos-section">
              <h3 className="section-title">Project Videos</h3>

              {/* MAIN VIDEO PLAYER */}
              <div className="main-video-container">
                <iframe
                  src={getEmbedUrl(project.videos[activeVideoIndex])}
                  title={`${project.title} - Video ${activeVideoIndex + 1}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="video-iframe"
                />
              </div>

              {/* VIDEO THUMBNAILS */}
              {project.videos.length > 1 && (
                <div className="video-thumbnails">
                  {project.videos.map((video, index) => (
                    <div
                      key={index}
                      className={`video-thumb ${
                        activeVideoIndex === index ? "active" : ""
                      }`}
                      onClick={() => setActiveVideoIndex(index)}
                    >
                      <img
                        src={getVideoThumbnail(video)}
                        alt={`Video ${index + 1}`}
                      />
                      <div className="play-overlay">
                        <FaPlay />
                      </div>
                      <span className="video-number">Video {index + 1}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT SIDE - DETAILS */}
        <div className="details-section">
          <div className="details-card">
            <h3 className="section-title">Project Description</h3>
            <div
              className="description-text"
              dangerouslySetInnerHTML={{ __html: project.description }}
            />

            {/* ADDITIONAL INFO */}
            {(project.metaTitle ||
              project.metaKeywords ||
              project.metaDescription) && (
              <div className="additional-info">
                <h3 className="section-title">Additional Information</h3>

                {project.metaTitle && (
                  <div className="info-item">
                    <strong>Title</strong>
                    <p>{project.metaTitle}</p>
                  </div>
                )}

                {project.metaKeywords && (
                  <div className="info-item">
                    <strong>Keywords</strong>
                    <p>{project.metaKeywords}</p>
                  </div>
                )}

                {project.metaDescription && (
                  <div className="info-item">
                    <strong>Meta Description</strong>
                    <p>{project.metaDescription}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* LIGHTBOX MODAL */}
      {lightboxOpen && project.images && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="lightbox-close"
            onClick={() => setLightboxOpen(false)}
          >
            <FaTimes />
          </button>

          <button
            className="lightbox-nav lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage("prev");
            }}
          >
            <FaChevronLeft />
          </button>

          <button
            className="lightbox-nav lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage("next");
            }}
          >
            <FaChevronRight />
          </button>

          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`${BASE_URL}${project.images[selectedImage]}`}
              alt={project.title}
              className="lightbox-image"
            />
            <div className="lightbox-counter">
              {selectedImage + 1} / {project.images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectDetails;
